import { makeStyles, createStyles, useTheme } from "@mui/styles";
import { Chip, Grid, Paper, Theme, TableCell } from "@mui/material";
import { TableHeader, TableBody } from "components";
import ShowMore from "components/ShowMore";
import React from "react";
import { Link as ReactLink } from "react-router-dom";
import { CompanyData, HeadcountGrowth, CompanyTableColumns, Vertical } from "types";

const useStyles = makeStyles<Theme>(theme =>
  createStyles({
    secondaryHeading: {
      fontSize: theme.typography.pxToRem(15),
      color: theme.palette.text.secondary
    },
    fullWidth: {
      width: "100%"
    },
    paperRoot: {
      width: "100%",
      marginBottom: theme.spacing(2),
      textAlign: "center"
    },
    linkText: {
      fontSize: theme.typography.pxToRem(14),
      paddingRight: "5px",
      color: theme.palette.info.main
    },
    chipStyle: {
      fontSize: theme.typography.pxToRem(14),
      marginRight: "5px",
      marginBottom: "5px",
      color: theme.palette.info.main,
      borderColor: theme.palette.info.main
    },
    imageStyle: {
      maxWidth: "200px",
      height: "auto"
    },
    imageFallBackStyle: {
      paddingTop: "25px",
      paddingBottom: "25px",
      maxWidth: "200px",
      height: "auto"
    }
  })
);
// const filterImages = (src: string) => {
//   if (!src || src.includes("data:")) {
//     return "/images/default-corporate-image.jpg";
//   } else {
//     return src;
//   }
// };

interface PropTypes {
  data: CompanyData;
}

const CompanyHeader = (props: PropTypes) => {
  const classes = useStyles();
  //eslint-disable-next-line
  const theme = useTheme();
  const { data } = props;

  const columns: CompanyTableColumns<CompanyData>[] = [
    {
      id: "namefinal",
      label: "Company Name",
      minWidth: 110
    },
    {
      id: "totalstrength_nn",
      label: "Overall Company Signal",
      minWidth: 110,
      align: "center",
      format: () => "Coming soon..."
    },

    {
      id: "headcountgrowth",
      label: "Employee Count",
      minWidth: 110,
      align: "center",
      format: value => {
        const _value = value as HeadcountGrowth[];
        if (_value) {
          for (const headcount of _value) {
            if (headcount.industryname === "All") {
              return headcount.departmentcount;
            }
          }
        } else {
          return "No Data";
        }
      }
    },
    {
      id: "locationfinal",
      label: "Location",
      minWidth: 110
    }
  ];
  const columns2: CompanyTableColumns<{ verticals: Vertical[] }>[] = [
    {
      id: "verticals",
      label: "Industries",
      width: "50%",
      format: values =>
        values && typeof values === "object" && values.length > 0 ? (
          <ShowMore
            isInline={true}
            data={values.map(valueOg => {
              const { vertical } = valueOg as Vertical;
              return (
                <Chip
                  label={vertical}
                  className={classes.chipStyle}
                  clickable
                  color="default"
                  variant="outlined"
                  onClick={(event: React.MouseEvent) => event.preventDefault()}
                  component={ReactLink}
                  to={{
                    pathname: "/search"
                  }}
                />
              );
            })}
            trimIndex={3}
          ></ShowMore>
        ) : (
          "No Industries"
        )
    }
  ];

  return (
    <div className={classes.fullWidth}>
      <Grid container spacing={2}>
        <Grid item xs={4} lg={3} xl={2}>
          <Paper className={classes.paperRoot}>
            <img
              className={classes.imageStyle}
              src={"//logo.clearbit.com/" + data.websitefinal}
              alt = "logo"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = "/images/default-corporate-image.jpg"; // some replacement image
                //e.currentTarget.style = 'padding-top: 25px; padding-bottom: 25px;' // inline styles in html format
              }}
            ></img>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={8} lg={9} xl={10}>
          <Grid container>
            <div className={classes.fullWidth}>
              <Grid item xs={12}>
                <Paper className={classes.paperRoot}>
                  <TableHeader
                    columns={columns}
                    renderColumns={column => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          width: column.width ? column.width : ""
                        }}
                      >
                        {column.label}
                      </TableCell>
                    )}
                    body={
                      <TableBody
                        data={[data]}
                        columns={columns}
                        renderRecords={(d, column) => (
                          <TableCell key={column.id} align={column.align}>
                            {column.format ? column.format(d[column.id]) : d[column.id]}
                          </TableCell>
                        )}
                      />
                    }
                  />
                </Paper>
                <Grid item xs={12}>
                  <Paper className={classes.paperRoot}>
                    <TableHeader
                      columns={columns2}
                      renderColumns={column => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                            width: column.width ? column.width : ""
                          }}
                        >
                          {column.label}
                        </TableCell>
                      )}
                      body={
                        <TableBody
                          data={[{ verticals: data.verticals ?? [] }]}
                          columns={columns2}
                          renderRecords={(d, column) => (
                            <TableCell key={column.id} align={column.align}>
                              {column.format ? column.format(d[column.id]) : d[column.id]}
                            </TableCell>
                          )}
                        />
                      }
                    />
                  </Paper>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default CompanyHeader;
